import * as React from 'react';

import { Col, Form, Input, Row, Select } from 'antd';

import {
  AntdUploadImgCrop,
  CheckInputbox,
  DecimalInputNumber,
} from 'components';
import { usePaymentGatewayAction, usePaymentGatewayValue } from 'context';
import type { PaymentGatewayDiscountType } from 'types';
import {
  generateDownloadFile,
  inputRule,
  localizePaymentGatewayDiscountType,
} from 'utils';

const paymentGatewayDiscountTypes = [
  'Percentage',
  'Price',
] satisfies PaymentGatewayDiscountType[];

type FieldType = Pick<
  ReturnType<typeof usePaymentGatewayValue>,
  | 'iconId'
  | 'title'
  | 'description'
  | 'enable'
  | 'discountType'
  | 'discountAmount'
>;

const PaymentGatewayFormContent = () => {
  const {
    title,
    description,
    iconId,
    enable,
    ownerType,
    discountType,
    discountAmount,
  } = usePaymentGatewayValue();
  const action = usePaymentGatewayAction();

  const { label: discountTypeLabel } =
    localizePaymentGatewayDiscountType(discountType);

  if (!ownerType) return <React.Fragment />;

  return (
    <Row className='px-6 py-8'>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='عنوان'
          name='title'
          rules={[inputRule.required, inputRule.string]}
          initialValue={title}
        >
          <Input placeholder='عنوان درگاه پرداخت را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='فعال‌سازی'
          name='enable'
          valuePropName='checked'
          initialValue={enable}
        >
          <CheckInputbox>فعال‌سازی درگاه پرداخت</CheckInputbox>
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نوع تخفیف'
          name='discountType'
          initialValue={discountType}
        >
          <Select<PaymentGatewayDiscountType | undefined>
            allowClear
            placeholder='نوع تخفیف درگاه پرداخت را انتخاب کنید'
            options={paymentGatewayDiscountTypes.map(type => {
              const { label } = localizePaymentGatewayDiscountType(type);
              return {
                label,
                value: type,
              };
            })}
            onChange={value => {
              action({
                type: 'UPDATE_PAYMENT_GATEWAY_DISCOUNT_TYPE',
                payload: {
                  discountType: value,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='مقدار تخفیف'
          name='discountAmount'
          rules={[inputRule.digit]}
          extra='تخفیف فقط برای درگاه‌های مستقیم (غیرقسطی) اعمال می‌شود.'
        >
          <DecimalInputNumber
            disabled={!discountType}
            addonAfter={discountTypeLabel}
            max={discountType === 'Percentage' ? 100 : undefined}
            placeholder='مقدار تخفیف درگاه پرداخت را وارد کنید'
            onChange={value => {
              action({
                type: 'UPDATE_PAYMENT_GATEWAY_DISCOUNT_AMOUNT',
                payload: {
                  discountAmount: value === null ? undefined : Number(value),
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='توضیحات مختصر'
          name='description'
          rules={[inputRule.string, inputRule.abstraction]}
          initialValue={description}
        >
          <Input.TextArea
            autoSize
            placeholder='توضیحات مختصر درگاه پرداخت را وارد کنید'
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='نشان‌واره (Icon)'
          name='iconId'
          valuePropName='file'
        >
          <AntdUploadImgCrop
            ownerType={ownerType}
            fileList={
              iconId
                ? [
                    {
                      uid: iconId,
                      name: 'نشان‌واره درگاه پرداخت',
                      status: 'done',
                      url: generateDownloadFile(ownerType, iconId),
                    },
                  ]
                : undefined
            }
            onChange={({ file: { status, response } }) => {
              action({
                type: 'UPDATE_PAYMENT_GATEWAY_ICON',
                payload: {
                  iconId: undefined,
                },
              });

              if (status === 'done' && response) {
                const { id } = response;
                action({
                  type: 'UPDATE_PAYMENT_GATEWAY_ICON',
                  payload: {
                    iconId: id,
                  },
                });
              }
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { PaymentGatewayFormContent };
